import { EnvironmentProviders, inject, makeEnvironmentProviders } from '@angular/core';

import { SubscriptionFacade } from './application/subscription.facade';
import { SubscriptionRepository } from './domain/interfaces/subscription.interface';
import { SUBSCRIPTION_CONFIG } from './infrastructure/subscription.config';
import { SubscriptionHttpRepository } from './infrastructure/repositories/subscription.http.repository';
import { SubscriptionMockRepository } from './infrastructure/repositories/subscription.mock.repository';

/**
 * provideSubscription — mounts the ROUTE-scoped {@link SubscriptionFacade} on
 * `/v2/subscription` (it owns screen + checkout-draft state, which belongs to a
 * single visit of the surface).
 *
 * The data layer is deliberately NOT here. `SubscriptionRepository` (and its
 * mock/http implementations) and `SubscriptionStore` are ROOT-provided (see
 * {@link provideSubscriptionData}, mounted at the TRUE app root in
 * `app.config.ts`), because subscription state gates UI far outside this
 * route — the Profile tier row, premium badges, Khoyek's daily cap. One root
 * store keeps them all reading the same signals; a route-scoped copy would
 * let them drift.
 * @see SubscriptionStore
 */
export function provideSubscription(): EnvironmentProviders {
  return makeEnvironmentProviders([SubscriptionFacade]);
}

/**
 * provideSubscriptionData — the ROOT-wide `SubscriptionRepository` binding
 * (config-driven mock/http selection). Lives here, not on the abstract class
 * itself, so `domain/interfaces/subscription.interface.ts` never imports
 * infrastructure (see that file's own doc comment, and
 * `tools/scripts/check-architecture.mjs`'s layer-direction rule).
 *
 * Mounted at the TRUE app root in `app.config.ts`'s `appConfig.providers` —
 * NOT inside `providePrimaryApp()`. `SubscriptionStore` is a plain `@Service()`
 * (`providedIn: 'root'`-equivalent), so it is constructed against the real
 * root injector; a route-level injector (which is what `providePrimaryApp()`
 * provides, scoped to `PRIMARY_ROUTES`'s `path: ''`) is invisible to it even
 * though every route is nested under that same tree. Registering this only
 * at the route level previously caused NG0201 for any root-scoped consumer
 * that forced `SubscriptionStore` to construct.
 */
export function provideSubscriptionData(): EnvironmentProviders {
  return makeEnvironmentProviders([
    SubscriptionMockRepository,
    SubscriptionHttpRepository,
    {
      provide: SubscriptionRepository,
      useFactory: () =>
        inject(SUBSCRIPTION_CONFIG).useMock
          ? inject(SubscriptionMockRepository)
          : inject(SubscriptionHttpRepository),
    },
  ]);
}
